import { SelectionToolbar } from '@/components/selection-bar/SelectionToolbar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useIsMobile } from '@/hooks/use-mobile'
import { useUIOverlayState } from '@/hooks/useIsDialogOpen'
import { cn } from '@/lib/utils'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
	DndContext,
	DragOverlay,
	PointerSensor,
	TouchSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import type { Header, Row } from '@tanstack/react-table'
import { flexRender } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowDown, ArrowDownUp, ArrowUp, GripVertical } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { BOTTOM_BAR_COLLAPSE_EVENT, BOTTOM_BAR_ID, BOTTOM_BAR_OPEN_CLASS } from '../bottom-bar/constants'
import { DraggableColumnHeader } from './DraggableColumnHeader'
import { TruncatedCell } from './TruncatedCell'
import type { TableViewProps } from './types'

export interface ColumnMeta {
	alignment?: 'left' | 'center' | 'right'
	fixedWidth?: boolean
	disableTruncation?: boolean // If true, don't wrap cell content in TruncatedCell
	headerTooltip?: string // Tooltip shown on column header hover to describe the column
	label?: string // Plain text name of the column, used where a rendered header cannot be (accessible sort label)
}

// A column whose `header` is a component has no readable text, so fall back to the meta label, then the column id.
export const getColumnSortLabel = <TData,>(header: Header<TData, unknown>) => {
	const { header: headerDef, meta } = header.column.columnDef
	return typeof headerDef === 'string' ? headerDef : ((meta as ColumnMeta | undefined)?.label ?? header.column.id)
}

// Helper function to get CSS classes for alignment
export const getAlignmentClasses = (alignment: string) => {
	switch (alignment) {
		case 'center':
			return 'justify-center text-center'
		case 'right':
			return 'justify-end text-right'
		case 'left':
			return 'justify-start text-left'
		default:
			return 'justify-start text-left'
	}
}

export function TableView<TData>({
	table,
	getRowId,
	frozenColumnIds,
	frozenRightColumnIds,
	staticColumnIds,
	enableSelection = false,
	selectionState,
	selectionToolbarProps,
	enableColumnReordering = false,
	onColumnReorder,
	onRowClick,
	onRowDoubleClick,
	onRowRightClick,
	onRowKeySelect,
	onGroupRowKeySelect,
	highlightedRowId,
	enableGrouping = false,
	preserveSelectionToolbarInBottomBar = false,
	onScroll,
	dataTestId,
}: TableViewProps<TData>) {
	const tableContainerRef = useRef<HTMLDivElement>(null)
	const headerScrollRef = useRef<HTMLDivElement>(null)
	const bodyRef = useRef<HTMLDivElement>(null)
	const headerContentRef = useRef<HTMLDivElement>(null)
	const hoveredGroupRowRef = useRef<Row<TData> | null>(null)
	const [scrollbarWidth, setScrollbarWidth] = useState(0)
	const [containerWidth, setContainerWidth] = useState(0)
	// drag state management
	const [activeColumnId, setActiveColumnId] = useState<string | null>(null)
	const [isDragActive, setIsDragActive] = useState(false)
	// touch detection
	const [isTouchDevice, setIsTouchDevice] = useState(false)
	const [headerHovered, setHeaderHovered] = useState(false)
	// Track UI overlay states (dialog and bottom bar) to hide selection toolbar
	const { isDialogOpen, isBottomBarOpen } = useUIOverlayState()
	// Measured height of the scroll body (for needsVerticalScroll calculation)
	const [bodyHeight, setBodyHeight] = useState(400)

	const isMobile = useIsMobile()

	useEffect(() => {
		setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0)
	}, [])

	// Measure scroll body height for vertical scrollbar / column sizing
	useEffect(() => {
		const el = bodyRef.current
		if (!el) {
			return
		}
		const ro = new ResizeObserver(() => {
			setBodyHeight(el.clientHeight)
		})
		ro.observe(el)
		setBodyHeight(el.clientHeight)
		return () => ro.disconnect()
	}, [])

	const rows = table.getRowModel().rows
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const headers = table.getHeaderGroups()[0]?.headers || []

	// Helper function to check if header has fixed width - moved outside useMemo to avoid re-render issues
	const staticColumnSet = useMemo(() => new Set(staticColumnIds ?? []), [staticColumnIds])
	const hasFixedWidth = useCallback(
		(header: Header<TData, unknown>) => {
			const meta = header.column.columnDef.meta as ColumnMeta | undefined
			return meta?.fixedWidth === true || staticColumnSet.has(header.column.id)
		},
		[staticColumnSet],
	)

	// Configurable frozen columns or default to first 2 columns
	const finalFrozenColumnIds = useMemo(() => {
		if (frozenColumnIds) {
			const currentColumnIdSet = new Set(headers.map(h => h.column.id))
			return frozenColumnIds.filter(id => currentColumnIdSet.has(id))
		}
		const defaultFrozenCount = isMobile ? 1 : 2
		return headers.slice(0, defaultFrozenCount).map(h => h.column.id)
	}, [frozenColumnIds, headers, isMobile])

	const frozenColumnSet = useMemo(() => new Set(finalFrozenColumnIds), [finalFrozenColumnIds])

	// Right-side frozen columns
	const finalFrozenRightColumnIds = useMemo(() => {
		if (frozenRightColumnIds) {
			const currentColumnIdSet = new Set(headers.map(h => h.column.id))
			return frozenRightColumnIds.filter(id => currentColumnIdSet.has(id))
		}
		return []
	}, [frozenRightColumnIds, headers])

	const frozenRightColumnSet = useMemo(() => new Set(finalFrozenRightColumnIds), [finalFrozenRightColumnIds])

	// Only exclude frozen columns from draggable list
	const draggableColumnSet = useMemo(() => {
		const draggableIds = headers
			.filter(header => !frozenColumnSet.has(header.column.id) && !frozenRightColumnSet.has(header.column.id))
			.map(header => header.column.id)

		return new Set(draggableIds)
	}, [headers, frozenColumnSet, frozenRightColumnSet])

	// ALL column IDs for SortableContext (includes frozen columns but they won't be draggable)
	const allColumnIds = useMemo(() => {
		return headers.map(header => header.column.id)
	}, [headers])

	/**
	 * Calculates the left position offset for sticky/frozen columns.
	 */
	const getFrozenLeftOffset = useCallback(
		(columnIndex: number, columnWidths: number[]) => {
			return columnWidths.slice(0, columnIndex).reduce((acc, width, i) => {
				return frozenColumnSet.has(headers[i]?.column.id || '') ? acc + width : acc
			}, 0)
		},
		[frozenColumnSet, headers],
	)

	/**
	 * Calculates the right position offset for right-side sticky/frozen columns.
	 */
	const getFrozenRightOffset = useCallback(
		(columnIndex: number, columnWidths: number[]) => {
			return columnWidths.slice(columnIndex + 1).reduce((acc, width, i) => {
				const actualIndex = columnIndex + 1 + i
				return frozenRightColumnSet.has(headers[actualIndex]?.column.id || '') ? acc + width : acc
			}, 0)
		},
		[frozenRightColumnSet, headers],
	)

	// Calculate scrollbar width for perfect header-body alignment
	useEffect(() => {
		const calculateScrollbarWidth = () => {
			const outer = document.createElement('div')
			outer.style.visibility = 'hidden'
			outer.style.overflow = 'scroll'
			outer.style.width = '100px'
			outer.style.height = '100px'

			outer.style.setProperty('-ms-overflow-style', 'scrollbar')
			document.body.appendChild(outer)

			const inner = document.createElement('div')
			inner.style.width = '100%'
			inner.style.height = '100%'
			outer.appendChild(inner)

			const scrollbarWidth = outer.offsetWidth - inner.offsetWidth
			document.body.removeChild(outer)

			return scrollbarWidth
		}

		setScrollbarWidth(calculateScrollbarWidth())
	}, [])

	// Monitor container width changes
	useEffect(() => {
		const updateContainerWidth = () => {
			if (tableContainerRef.current) {
				setContainerWidth(tableContainerRef.current.offsetWidth)
			}
		}

		updateContainerWidth()

		const resizeObserver = new ResizeObserver(updateContainerWidth)
		if (tableContainerRef.current) {
			resizeObserver.observe(tableContainerRef.current)
		}

		return () => {
			resizeObserver.disconnect()
		}
	}, [])

	const virtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => bodyRef.current,
		// Source of truth for table row height. The virtualizer positions rows by absolute
		// pixel offsets, so this value overrides any CSS height applied to <tr>/<td>.
		// Keep in sync with --height-table-row in src/index.css.
		estimateSize: () => 32,
		overscan: 5,
		// Tell the virtualizer the body height up front. On a fresh mount it hasn't
		// measured the body yet, so without this it assumes height 0 and draws only a
		// few rows for one frame before filling the page — a visible flash.
		initialRect: { width: containerWidth, height: bodyHeight },
		getItemKey: getRowId
			? index => {
					const row = rows[index]
					return getRowId(row.original)
				}
			: undefined,
	})

	const virtualItems = virtualizer.getVirtualItems()

	// Check if a row is selected or highlighted
	const isRowSelected = useCallback(
		(row: Row<TData>): boolean => {
			// For highlighting, check both TanStack row.id and computed rowId
			// This handles both group header rows and leaf rows
			if (highlightedRowId) {
				// Check TanStack's internal row ID (for group rows)
				if (row.id === highlightedRowId) {
					return true
				}
				// Check computed rowId from getRowId (for leaf rows)
				const rowId = getRowId ? getRowId(row.original) : row.id
				if (rowId && rowId === highlightedRowId) {
					return true
				}
			}
			// Check standard selection state
			if (!enableSelection || !selectionState) {
				return false
			}
			const rowId = getRowId ? getRowId(row.original) : row.id
			return selectionState.isSelected(rowId)
		},
		[enableSelection, selectionState, getRowId, highlightedRowId],
	)

	// Bidirectional scroll synchronization
	const syncScroll = useCallback((source: 'header' | 'body', scrollLeft: number) => {
		if (source === 'body' && headerScrollRef.current) {
			headerScrollRef.current.scrollLeft = scrollLeft
		} else if (source === 'header' && bodyRef.current) {
			bodyRef.current.scrollLeft = scrollLeft
		}
	}, [])

	// Scroll event listeners for real-time sync
	useEffect(() => {
		const headerElement = headerScrollRef.current
		const bodyElement = bodyRef.current

		if (!headerElement || !bodyElement) {
			return
		}

		const handleHeaderScroll = () => {
			syncScroll('header', headerElement.scrollLeft)
		}

		const handleBodyScroll = () => {
			syncScroll('body', bodyElement.scrollLeft)
		}

		headerElement.addEventListener('scroll', handleHeaderScroll)
		bodyElement.addEventListener('scroll', handleBodyScroll)

		return () => {
			headerElement.removeEventListener('scroll', handleHeaderScroll)
			bodyElement.removeEventListener('scroll', handleBodyScroll)
		}
	}, [syncScroll])

	// Base column width calculations - now properly includes hasFixedWidth in dependencies
	const { baseColumnWidths, baseTotalWidth } = useMemo(() => {
		const baseColumnWidths = headers.map(header => {
			const originalSize = header.getSize()

			if (header.id === 'select') {
				return 30
			}
			if (isMobile) {
				if (hasFixedWidth(header)) {
					return originalSize
				}
				const minMobileWidth = 120
				return Math.max(originalSize, minMobileWidth)
			}
			return originalSize
		})

		const baseTotalWidth = baseColumnWidths.reduce((sum, width) => sum + width, 0)

		return {
			baseColumnWidths,
			baseTotalWidth,
		}
	}, [headers, isMobile, hasFixedWidth])

	// Dynamic column sizing calculations - prevents fixed columns from being expanded.
	// Expands non-fixed columns only when there is extra available width and no horizontal scroll is needed.
	const { columnWidths, totalWidth, shouldExpandColumns, needsVerticalScroll, contentHeight } = useMemo(() => {
		const contentHeight = virtualizer.getTotalSize()
		const needsVerticalScroll = contentHeight > bodyHeight

		const availableWidth = containerWidth - (needsVerticalScroll ? scrollbarWidth : 0)
		const needsHorizontalScroll = baseTotalWidth > availableWidth

		const shouldExpandColumns =
			baseTotalWidth < availableWidth && availableWidth > 0 && !needsHorizontalScroll && !isMobile

		let columnWidths = baseColumnWidths
		let totalWidth = baseTotalWidth

		if (shouldExpandColumns) {
			const extraWidth = availableWidth - baseTotalWidth

			// Get expandable columns (non-fixed, non-select)
			const expandableColumns = headers.filter(h => h.id !== 'select' && !hasFixedWidth(h))
			const expandableColumnsCount = expandableColumns.length

			// Only distribute extra space among expandable columns
			const extraPerColumn = expandableColumnsCount > 0 ? Math.floor(extraWidth / expandableColumnsCount) : 0

			columnWidths = baseColumnWidths.map((width, index) => {
				const header = headers[index]
				// Keep fixed-width columns and select column at their original size
				if (header.id === 'select' || hasFixedWidth(header)) {
					return width
				}
				// Expand only non-fixed columns
				return width + extraPerColumn
			})

			totalWidth = columnWidths.reduce((sum, width) => sum + width, 0)
		}

		return {
			columnWidths,
			totalWidth,
			shouldExpandColumns,
			needsVerticalScroll,
			contentHeight,
		}
	}, [
		baseColumnWidths,
		baseTotalWidth,
		headers,
		containerWidth,
		scrollbarWidth,
		bodyHeight,
		virtualizer,
		isMobile,
		hasFixedWidth,
	])

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: isMobile ? 12 : 8,
			},
		}),
		useSensor(TouchSensor, {
			activationConstraint: {
				delay: isMobile ? 500 : 300,
				tolerance: isMobile ? 15 : 10,
			},
		}),
	)

	const cleanupDragState = useCallback(() => {
		setActiveColumnId(null)
		setIsDragActive(false)
		document.body.classList.remove('is-dragging')

		if (isTouchDevice && tableContainerRef.current) {
			tableContainerRef.current.style.touchAction = ''
		}
	}, [isTouchDevice])

	const handleDragStart = useCallback(
		(event: DragStartEvent) => {
			const { active } = event

			if (!draggableColumnSet.has(String(active.id))) {
				return
			}

			setActiveColumnId(String(active.id))
			setIsDragActive(true)
			document.body.classList.add('is-dragging')

			if (isTouchDevice && tableContainerRef.current) {
				tableContainerRef.current.style.touchAction = 'none'
			}
		},
		[draggableColumnSet, isTouchDevice],
	)

	const handleDragCancel = useCallback(() => {
		cleanupDragState()
	}, [cleanupDragState])

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event

			try {
				if (!over) {
					return
				}
				const activeId = String(active.id)
				const overId = String(over.id)

				if (activeId === overId) {
					return
				}
				if (frozenColumnSet.has(overId) || frozenRightColumnSet.has(overId)) {
					return
				}
				if (!draggableColumnSet.has(activeId) || !draggableColumnSet.has(overId)) {
					return
				}
				if (enableColumnReordering && onColumnReorder) {
					onColumnReorder(activeId, overId)
				}
			} finally {
				cleanupDragState()
			}
		},
		[
			frozenColumnSet,
			frozenRightColumnSet,
			draggableColumnSet,
			enableColumnReordering,
			onColumnReorder,
			cleanupDragState,
		],
	)

	const [fixedToolbarPosition, setFixedToolbarPosition] = useState<{ x: number; y: number } | null>(null)

	const getAnchorPosition = useCallback(
		(options: { type: 'header' } | { type: 'row'; rowId: string }): { x: number; y: number } | null => {
			const anchorColumnId = selectionToolbarProps?.anchorColumnId
			const anchorToFirstColumn = selectionToolbarProps?.anchorToFirstColumn
			const targetColumnId =
				anchorColumnId || (anchorToFirstColumn ? headers[0]?.id : headers.find(h => h.id !== 'select')?.id)

			if (!targetColumnId) {
				return null
			}

			if (options.type === 'header') {
				if (!headerScrollRef.current) {
					return null
				}
				const columnElement = headerScrollRef.current.querySelector(`[data-column-id="${targetColumnId}"]`)
				if (columnElement) {
					const rect = columnElement.getBoundingClientRect()
					return {
						x: rect.right + 10,
						y: rect.top + rect.height / 2 - 20, // Center vertically with toolbar height ~40px
					}
				}
				return null
			}

			// Row anchor positioning
			const { rowId } = options
			if (!bodyRef.current) {
				return null
			}

			const columnElement = bodyRef.current.querySelector(
				`[data-row-id="${rowId}"] [data-column-id="${targetColumnId}"]`,
			)

			if (!columnElement) {
				return null
			}

			const rect = columnElement.getBoundingClientRect()
			return {
				x: rect.right + 10,
				y: rect.top + rect.height / 2 - 20, // Center vertically with toolbar height ~40px
			}
		},
		[headers, selectionToolbarProps?.anchorColumnId, selectionToolbarProps?.anchorToFirstColumn],
	)

	// useEffect to capture and maintain the toolbar position based on anchor geometry
	useEffect(() => {
		if (!selectionState || selectionState.selectedCount === 0) {
			setFixedToolbarPosition(null)
			return
		}

		// Function to get anchor position with fallback to header if row is not present
		const resolvePosition = () => {
			// If all rows are selected, we always anchor to the header
			if (selectionState.isAllSelected) {
				return getAnchorPosition({ type: 'header' })
			}

			// Try to anchor to the last interaction point (last checked row)
			const anchorRowId =
				selectionState.lastCheckedRowId ||
				(selectionState.selectedRowIds.length > 0
					? selectionState.selectedRowIds[selectionState.selectedRowIds.length - 1]
					: null)

			if (anchorRowId) {
				const rowPos = getAnchorPosition({ type: 'row', rowId: anchorRowId })
				if (rowPos) {
					return rowPos
				}
			}

			// Fallback to header position if the anchor row is not present on the current page
			// (e.g. across pagination or after filtering)
			return getAnchorPosition({ type: 'header' })
		}

		setFixedToolbarPosition(resolvePosition())
	}, [selectionState, getAnchorPosition])

	const [hoveredRow, setHoveredRow] = useState<TData | null>(null)

	// keyboard event listener for Space key
	useEffect(() => {
		const isTypingTarget = (target: EventTarget | null) => {
			if (!(target instanceof HTMLElement)) {
				return false
			}
			return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (isTypingTarget(event.target)) {
				return
			}

			if (event.key === 'x') {
				if (hoveredRow) {
					if (enableSelection && selectionState && getRowId) {
						event.preventDefault()
						// Respect selectableRowFilter — rows with a disabled checkbox must not be
						// selectable via the keyboard either.
						if (selectionState.isRowSelectable(hoveredRow)) {
							selectionState.toggleRow(getRowId(hoveredRow), hoveredRow)
						}
					} else if (onRowKeySelect) {
						event.preventDefault()
						onRowKeySelect(hoveredRow)
					}
				} else if (hoveredGroupRowRef.current && onGroupRowKeySelect) {
					event.preventDefault()
					const leafRows = hoveredGroupRowRef.current.getLeafRows().map(r => r.original)
					onGroupRowKeySelect(leafRows)
				}
				return
			}

			if (event.code !== 'Space') {
				return
			}

			// Check if the bottom bar is currently open (full-page).
			// Read DOM directly for a synchronous, always-fresh check.
			const bottomBar = document.getElementById(BOTTOM_BAR_ID)
			const isBarCurrentlyOpen = bottomBar?.classList.contains(BOTTOM_BAR_OPEN_CLASS) ?? false

			if (isBarCurrentlyOpen) {
				// Collapse the bar — no hoveredRow needed since the full-page bar
				// covers the table, clearing hoveredRow via onMouseLeave.
				event.preventDefault()
				window.dispatchEvent(new CustomEvent(BOTTOM_BAR_COLLAPSE_EVENT))
				return
			}

			// Bar is closed — only open if user is hovering a row
			if (hoveredRow && onRowDoubleClick) {
				event.preventDefault()
				onRowDoubleClick(hoveredRow)
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => {
			window.removeEventListener('keydown', handleKeyDown)
		}
	}, [hoveredRow, onRowDoubleClick, selectionState, enableSelection, getRowId, onRowKeySelect, onGroupRowKeySelect])

	const headerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (headerRef.current) {
			const height = headerRef.current.offsetHeight
			tableContainerRef.current?.style.setProperty('--table-header-height', `${height}px`)
		}
	}, [])

	const tableContent = (
		<div
			ref={tableContainerRef}
			data-table-container='true'
			className='size-full flex flex-col min-w-0 bg-background'
			style={{ '--table-header-height': '40px' } as React.CSSProperties}>
			<div ref={headerRef} className='shrink-0 border-b bg-table-header-bg group'>
				<div
					ref={headerScrollRef}
					data-selection-safe-zone='true'
					className='overflow-x-auto scrollbar-hide text-body'
					style={{
						overflowY: 'hidden',
						scrollbarWidth: 'none',
						msOverflowStyle: 'none',
						paddingRight: needsVerticalScroll ? `${scrollbarWidth}px` : '0px',
						width: '100%',
					}}>
					<div
						ref={headerContentRef}
						onMouseEnter={() => setHeaderHovered(true)}
						onMouseLeave={() => setHeaderHovered(false)}
						className='flex items-center text-body'
						style={{
							width: shouldExpandColumns ? '100%' : `${totalWidth}px`,
							minWidth: '100%',
						}}>
						{enableColumnReordering ? (
							<SortableContext items={allColumnIds} strategy={horizontalListSortingStrategy}>
								{headers.map((header, index) => {
									const isFrozenLeft = frozenColumnSet.has(header.column.id)
									const isFrozenRight = frozenRightColumnSet.has(header.column.id)
									const isFrozen = isFrozenLeft || isFrozenRight
									const isDraggable = enableColumnReordering && !isFrozen
									const leftOffset = getFrozenLeftOffset(index, columnWidths)
									const rightOffset = getFrozenRightOffset(index, columnWidths)
									const alignment = (header.column.columnDef.meta as ColumnMeta | undefined)?.alignment || 'left'
									// Check if this is the first right-frozen column to add margin-left: auto
									const isFirstRightFrozen =
										isFrozenRight && (index === 0 || !frozenRightColumnSet.has(headers[index - 1]?.column.id))

									return (
										<DraggableColumnHeader
											key={`${header.column.id}-${header.depth}`}
											header={header}
											columnWidth={columnWidths[index]}
											isFrozen={isFrozen}
											isFrozenRight={isFrozenRight}
											leftOffset={leftOffset}
											rightOffset={rightOffset}
											isDraggable={isDraggable}
											isTouchDevice={isTouchDevice}
											alignment={alignment}
											isFirstRightFrozen={isFirstRightFrozen}
											isLastColumn={index === headers.length - 1}
										/>
									)
								})}
							</SortableContext>
						) : (
							headers.map((header, index) => {
								const isFrozenLeft = frozenColumnSet.has(header.column.id)
								const isFrozenRight = frozenRightColumnSet.has(header.column.id)
								const isFrozen = isFrozenLeft || isFrozenRight
								const leftOffset = getFrozenLeftOffset(index, columnWidths)
								const rightOffset = getFrozenRightOffset(index, columnWidths)
								const alignment = (header.column.columnDef.meta as ColumnMeta | undefined)?.alignment || 'left'
								const alignmentClasses = getAlignmentClasses(alignment)
								// Check if this is the first right-frozen column to add margin-left: auto
								const isFirstRightFrozen =
									isFrozenRight && (index === 0 || !frozenRightColumnSet.has(headers[index - 1]?.column.id))

								const canSort = header.column.getCanSort()
								const sortDirection = header.column.getIsSorted()
								const isSelectColumn = header.column.id === 'select'
								const headerTooltip = (header.column.columnDef.meta as ColumnMeta | undefined)?.headerTooltip
								const headerCell = (
									<TruncatedCell
										className='w-full font-medium'
										align={alignment as 'left' | 'center' | 'right'}
										showTooltip={!headerTooltip}>
										{flexRender(header.column.columnDef.header, header.getContext())}
									</TruncatedCell>
								)

								const handleSortClick = (event: React.MouseEvent) => {
									event.preventDefault()
									event.stopPropagation()

									if (canSort && !isSelectColumn) {
										header.column.toggleSorting()
									}
								}

								const handleSortKeyDown = (event: React.KeyboardEvent) => {
									if (event.key === 'Enter' || event.key === ' ') {
										event.preventDefault()
										event.stopPropagation()

										if (canSort && !isSelectColumn) {
											header.column.toggleSorting()
										}
									}
								}

								const showActiveSort = canSort && !isSelectColumn && sortDirection
								const showHoverSort = canSort && !isSelectColumn && !sortDirection

								const ActiveSortIcon = sortDirection === 'asc' ? ArrowUp : ArrowDown

								return (
									<div
										key={header.id}
										data-column-id={header.column.id}
										className={cn(
											'group relative flex items-center py-2 font-medium text-body text-foreground shrink-0 z-10',
											isSelectColumn ? 'px-2 justify-center' : 'px-4',
											alignmentClasses,
											isFrozen ? 'frozen-header-solid' : 'bg-table-header-bg',
											index === headers.length - 1 && 'flex-1',
										)}
										style={{
											width: `${columnWidths[index]}px`,
											...(isFirstRightFrozen && {
												marginLeft: 'auto',
											}),
											...(isFrozenLeft && {
												position: 'sticky',
												left: `${leftOffset}px`,
												zIndex: 20,
											}),
											...(isFrozenRight && {
												position: 'sticky',
												right: `${rightOffset}px`,
												zIndex: 20,
											}),
										}}>
										<div
											className={`flex-1 min-w-0 flex items-center ${alignmentClasses} transition-colors ${
												canSort && !isSelectColumn
													? 'cursor-pointer focus:outline-none focus:bg-accent/20 focus:ring-2 focus:ring-primary/20'
													: ''
											}`}
											onClick={handleSortClick}
											onKeyDown={handleSortKeyDown}
											tabIndex={canSort && !isSelectColumn ? 0 : -1}
											role={canSort && !isSelectColumn ? 'button' : undefined}
											aria-label={canSort && !isSelectColumn ? `Sort by ${getColumnSortLabel(header)}` : undefined}>
											{header.isPlaceholder ? null : headerTooltip ? (
												<Tooltip>
													<TooltipTrigger asChild>
														<span className='w-full min-w-0 flex'>{headerCell}</span>
													</TooltipTrigger>
													<TooltipContent side='top' sideOffset={4}>
														<p>{headerTooltip}</p>
													</TooltipContent>
												</Tooltip>
											) : (
												headerCell
											)}
										</div>

										<div
											className='absolute right-1 top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center cursor-pointer'
											style={{ pointerEvents: canSort && !isSelectColumn ? 'auto' : 'none' }}
											onClick={handleSortClick}
											onKeyDown={handleSortKeyDown}
											tabIndex={canSort && !isSelectColumn ? 0 : -1}
											role={canSort && !isSelectColumn ? 'button' : undefined}
											aria-label={canSort && !isSelectColumn ? `Sort by ${getColumnSortLabel(header)}` : undefined}>
											{showActiveSort && (
												<ActiveSortIcon
													className='size-(--icon-standalone) text-foreground'
													style={{ pointerEvents: 'none' }}
												/>
											)}

											{showHoverSort && (
												<ArrowDownUp
													className='size-(--icon-standalone) text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity'
													style={{ pointerEvents: 'none' }}
												/>
											)}
										</div>
									</div>
								)
							})
						)}
					</div>
				</div>
			</div>

			<div
				ref={bodyRef}
				className='flex-1 overflow-auto scrollbar-thin min-w-0 bg-card min-h-0 pb-24 select-none'
				onScroll={onScroll}>
				<div
					style={{
						height: contentHeight,
						minHeight: '100%',
						position: 'relative',
						width: shouldExpandColumns ? '100%' : `${totalWidth}px`,
						minWidth: '100%',
					}}>
					{virtualItems.map(virtualItem => {
						const row = rows[virtualItem.index]
						const rowKey = getRowId ? getRowId(row.original) : row.id
						const isSelected = isRowSelected(row)
						const cells = row.getVisibleCells()

						return (
							<div key={row.id} className='relative' data-row-id={rowKey}>
								<div
									style={{
										position: 'absolute',
										top: 0,
										left: 0,
										width: shouldExpandColumns ? '100%' : `${totalWidth}px`,
										height: virtualItem.size,
										transform: `translateY(${virtualItem.start}px)`,
									}}
									className={`flex group transition-colors cursor-pointer bg-table-row-bg ${
										isSelected
											? 'bg-table-row-selected-bg hover:bg-table-row-selected-hover-bg'
											: 'hover:bg-table-row-hover-bg'
									}`}
									onMouseEnter={() => {
										if (enableGrouping && row.getIsGrouped?.()) {
											hoveredGroupRowRef.current = row
											setHoveredRow(null)
											return
										}
										hoveredGroupRowRef.current = null
										setHoveredRow(row.original)
									}}
									onMouseLeave={() => {
										hoveredGroupRowRef.current = null
										setHoveredRow(null)
									}}
									onClick={() => {
										// Skip group header rows — single-click navigation is for leaf rows only.
										if (enableGrouping && row.getIsGrouped?.()) {
											return
										}
										onRowClick?.(row.original)
									}}
									onKeyDown={
										onRowClick
											? e => {
													if (e.key === 'Enter' || e.key === ' ') {
														if (enableGrouping && row.getIsGrouped?.()) {
															return
														}
														e.preventDefault()
														onRowClick(row.original)
													}
												}
											: undefined
									}
									onDoubleClick={() => onRowDoubleClick?.(row.original)}
									onContextMenu={e => {
										// Always prevent browser context menu on table rows
										e.preventDefault()
										// Compute the row ID - use TanStack's row.id for group rows, computed ID for leaf rows
										const isGroupRow = enableGrouping && row.getIsGrouped?.()
										const rowId = isGroupRow ? row.id : getRowId ? getRowId(row.original) : row.id
										// If selection is enabled and row not selected, select it (skip for group rows
										// and rows rejected by selectableRowFilter — their checkbox is disabled, so
										// right-click must not select them either)
										if (
											!isGroupRow &&
											enableSelection &&
											selectionState &&
											!isSelected &&
											selectionState.isRowSelectable(row.original)
										) {
											selectionState.replaceSelection(rowId, row.original)
										}
										// Call the callback for additional handling (e.g., showing contextual menu)
										onRowRightClick?.(row.original, e, rowId)
									}}>
									{enableGrouping && row.getIsGrouped() ? (
										// Full-width group row rendering - Span entire row width
										<>
											{cells.map(cell => {
												// ONLY render the grouped column cell.
												// We skip the selection column here because the group header
												// (StatusGroupHeader) usually contains its own selection logic.
												const isGroupedCell = cell.getIsGrouped()

												if (!isGroupedCell) {
													return null
												}

												return (
													<div
														key={cell.id}
														className={cn(
															'flex items-center text-body shrink-0 relative px-0 flex-1',
															isSelected ? 'frozen-cell-selected' : 'frozen-cell-solid',
														)}
														style={{
															width: 'auto', // Span remaining space (flex-1)
															position: 'sticky',
															left: 0,
															zIndex: 10,
															minWidth: shouldExpandColumns ? '100%' : `${totalWidth}px`,
														}}>
														<div className='flex items-center w-full'>
															{flexRender(cell.column.columnDef.cell, {
																...cell.getContext(),
																virtualIndex: row.index,
															})}
														</div>
													</div>
												)
											})}
										</>
									) : (
										// Standard row rendering
										cells.map((cell, index) => {
											const isFrozenLeft = frozenColumnSet.has(cell.column.id)
											const isFrozenRight = frozenRightColumnSet.has(cell.column.id)
											const isFrozen = isFrozenLeft || isFrozenRight
											const leftOffset = getFrozenLeftOffset(index, columnWidths)
											const rightOffset = getFrozenRightOffset(index, columnWidths)
											const meta = cell.column.columnDef.meta as ColumnMeta | undefined
											const cellAlignment = meta?.alignment || 'left'
											// Check if this is the first right-frozen cell to add margin-left: auto
											const isFirstRightFrozen =
												isFrozenRight && (index === 0 || !frozenRightColumnSet.has(cells[index - 1]?.column.id))

											return (
												<div
													key={cell.id}
													data-column-id={cell.column.id}
													className={cn(
														'flex items-center text-body shrink-0 relative',
														cell.column.id === 'select' ? 'justify-center px-0' : 'px-6',
														isFrozen ? (isSelected ? 'frozen-cell-selected' : 'frozen-cell-solid') : '',
														index === cells.length - 1 && 'flex-1',
													)}
													{...(cell.column.id === 'select' && { 'data-selection-safe-zone': 'true' })}
													style={{
														width: `${columnWidths[index]}px`,
														...(isFirstRightFrozen && {
															marginLeft: 'auto',
														}),
														...(isFrozenLeft && {
															position: 'sticky',
															left: `${leftOffset}px`,
															zIndex: 10,
														}),
														...(isFrozenRight && {
															position: 'sticky',
															right: `${rightOffset}px`,
															zIndex: 10,
														}),
													}}>
													{enableSelection && cell.column.id === 'select' && (
														<div
															data-row-checkbox='true'
															data-testid={dataTestId ? `${dataTestId}-row-checkbox-cell-${rowKey}` : undefined}
															className={cn(
																'flex items-center justify-center size-full transition-opacity duration-200',
																isMobile
																	? 'opacity-100'
																	: isSelected || headerHovered
																		? 'opacity-100'
																		: 'opacity-0 group-hover:opacity-100',
															)}>
															{flexRender(cell.column.columnDef.cell, {
																...cell.getContext(),
																virtualIndex: row.index,
															})}
														</div>
													)}

													{cell.column.id !== 'select' &&
														(meta?.disableTruncation ? (
															<div
																className={cn(
																	cellAlignment === 'center' && 'justify-center text-center',
																	cellAlignment === 'right' && 'justify-end text-right',
																	'flex items-center w-full',
																)}>
																{flexRender(cell.column.columnDef.cell, {
																	...cell.getContext(),
																	virtualIndex: row.index,
																})}
															</div>
														) : (
															<TruncatedCell align={cellAlignment}>
																{flexRender(cell.column.columnDef.cell, {
																	...cell.getContext(),
																	virtualIndex: row.index,
																})}
															</TruncatedCell>
														))}
												</div>
											)
										})
									)}
								</div>
							</div>
						)
					})}
				</div>
			</div>
			{/* Selection Toolbar: single portal outside row loop so only one menu is ever rendered */}
			{selectionToolbarProps &&
				selectionState &&
				selectionState.selectedCount > 0 &&
				fixedToolbarPosition &&
				!isDialogOpen &&
				(preserveSelectionToolbarInBottomBar ? isBottomBarOpen : !isBottomBarOpen) &&
				createPortal(
					<div
						data-testid={
							selectionToolbarProps.dataTestId ? `${selectionToolbarProps.dataTestId}-contextual-menu` : undefined
						}
						className='fixed z-80 flex items-center bg-card border border-border shadow-levitating rounded-md pointer-events-auto h-(--height-contextual-menu) selection-toolbar-portal'
						style={{
							left: `${fixedToolbarPosition.x}px`,
							top: `${fixedToolbarPosition.y}px`,
							maxWidth: 'calc(100vw - 20px)',
						}}>
						<SelectionToolbar
							{...selectionToolbarProps}
							preserveSelectionToolbarInBottomBar={preserveSelectionToolbarInBottomBar}
							tableContainerRef={tableContainerRef}
							selectedCount={selectionState.selectedCount}
							className={cn(
								selectionToolbarProps?.anchorToFirstColumn ? '' : 'whitespace-nowrap',
								'text-body',
								'left-0! top-0!',
							)}
							style={{ position: 'relative', transform: 'none', ...selectionToolbarProps?.style }}
							onClearSelection={() => {
								// Clear the table's internal selection so the toolbar hides; then notify parent.
								selectionState.deselectAll()
								selectionToolbarProps?.onClearSelection?.()
							}}
						/>
					</div>,
					document.body,
				)}
		</div>
	)

	// Conditional DndContext wrapper
	if (!enableColumnReordering) {
		return tableContent
	}

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			modifiers={[restrictToHorizontalAxis]}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
			onDragCancel={handleDragCancel}>
			{tableContent}

			<DragOverlay
				dropAnimation={{
					duration: 200,
					easing: 'ease-out',
				}}>
				{activeColumnId && isDragActive ? (
					<div className='drag-overlay bg-background border shadow-lg rounded px-4 py-2 font-medium text-body opacity-90'>
						<div className='flex items-center gap-icon-text'>
							<GripVertical className='size-(--icon-standalone) text-muted-foreground' />
							{activeColumnId}
						</div>
					</div>
				) : null}
			</DragOverlay>
		</DndContext>
	)
}
